import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { toast } from 'react-hot-toast';

import { getRoom } from '../../api/rooms';
import { DEFAULT_PLANNER_TITLE } from '../../constants/schedule';
import { createRoomLink } from '../../utils/linking';
import { buildInitialDeviceId } from '../../utils/persistence';
import {
  type StoredPlanner,
  readStoredPlanners,
  removeStoredPlanner,
  saveStoredPlanners,
} from '../../utils/schedule-storage';
import { logger } from '../../utils/logger';

type RoomPreviewPayload = { eventTitle?: string | null };

export type StoredPlannerRow = StoredPlanner & {
  url: string;
  createdAt: string;
};

const sortLinksByDate = (list: StoredPlannerRow[]) =>
  [...list].sort((a, b) => {
    const tsA = Date.parse(a.createdAt) || 0;
    const tsB = Date.parse(b.createdAt) || 0;
    return tsB - tsA;
  });

const normalizeRows = (
  saved: StoredPlanner[],
  prev: StoredPlannerRow[] = [],
): StoredPlannerRow[] => {
  const prevMap = new Map(prev.map((link) => [link.id, link]));
  const unsorted = saved.map((link) => {
    const prevLink = prevMap.get(link.id);
    const url = link.url || createRoomLink(link.id);
    return {
      ...link,
      url,
      title: link.title ?? prevLink?.title,
      createdAt: prevLink?.createdAt || link.createdAt,
    };
  });
  return sortLinksByDate(unsorted);
};

type UseStoredPlannersOptions = {
  isOpen: boolean;
  isSavedTabActive: boolean;
};

export function useStoredPlanners({
  isOpen,
  isSavedTabActive,
}: UseStoredPlannersOptions) {
  const [planners, setPlanners] = useState<StoredPlannerRow[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const plannersRef = useRef<StoredPlannerRow[]>([]);
  const deviceIdRef = useRef(buildInitialDeviceId());

  useEffect(() => {
    plannersRef.current = planners;
  }, [planners]);

  const loadFromStorage = useCallback(async () => {
    const saved = await readStoredPlanners();
    const normalized = normalizeRows(saved, plannersRef.current);
    plannersRef.current = normalized;
    setPlanners(normalized);
  }, []);

  const runRefresh = useCallback(async () => {
    const saved = await readStoredPlanners();
    const normalized = normalizeRows(saved, plannersRef.current);

    const deviceId = deviceIdRef.current;
    if (!deviceId || normalized.length === 0) {
      plannersRef.current = normalized;
      setPlanners(normalized);
      return;
    }

    const titleById = new Map<string, string>();
    const updatedPlanners: StoredPlanner[] = [];

    await Promise.all(
      normalized.map(async (link) => {
        try {
          const { payload } = await getRoom<RoomPreviewPayload>(
            link.id,
            deviceId,
          );

          const title =
            payload && typeof payload.eventTitle === 'string'
              ? payload.eventTitle.trim()
              : '';

          if (title) {
            titleById.set(link.id, title);
            updatedPlanners.push({
              id: link.id,
              url: link.url,
              title,
              createdAt: link.createdAt,
            });
          }
        } catch (error) {
          logger.error(`Failed to fetch room details for ${link.id}`, error);
        }
      }),
    );

    const merged = sortLinksByDate(
      normalized.map((link) => {
        const fetchedTitle = titleById.get(link.id);
        return {
          ...link,
          title: fetchedTitle || link.title,
        };
      }),
    );

    plannersRef.current = merged;
    setPlanners(merged);

    if (updatedPlanners.length > 0) {
      await saveStoredPlanners(updatedPlanners);
    }
  }, []);

  const refreshPlanners = useCallback(
    async (opts?: { showToast?: boolean }) => {
      if (refreshInFlightRef.current) {
        if (!opts?.showToast) return;
        await refreshInFlightRef.current;
      }

      if (opts?.showToast) {
        const saved = await readStoredPlanners();
        if (saved.length === 0) {
          toast.success('The planners are up to date');
          return;
        }
      }

      const promise = (async () => {
        setIsRefreshing(true);
        try {
          await runRefresh();
        } finally {
          setIsRefreshing(false);
        }
      })();

      refreshInFlightRef.current = promise;

      try {
        if (opts?.showToast) {
          await toast.promise(promise, {
            loading: 'Loading...',
            success: 'The planners are up to date',
            error: 'Failed to refresh saved planners',
          });
        } else {
          await promise;
        }
      } finally {
        refreshInFlightRef.current = null;
      }
    },
    [runRefresh],
  );

  useEffect(() => {
    if (!isOpen) return;
    if (isSavedTabActive) {
      void refreshPlanners();
    } else {
      void loadFromStorage();
    }
  }, [isOpen, isSavedTabActive, loadFromStorage, refreshPlanners]);

  const handleRemove = useCallback(async (id: string) => {
    await removeStoredPlanner(id);
    setPlanners((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const confirmRemove = useCallback(
    (id: string) => {
      const ok = confirm(
        'Remove Saved Link?\n\nThis only removes the saved link from this device. The planner itself remains available.',
      );
      if (ok) {
        void handleRemove(id);
      }
    },
    [handleRemove],
  );

  const copyOrShareLink = useCallback(async (item: StoredPlannerRow) => {
    const url = item.url || createRoomLink(item.id);
    const title = item.title?.trim() || DEFAULT_PLANNER_TITLE;

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if ((error as DOMException)?.name === 'AbortError') return;
        logger.error('Share failed, falling back to clipboard:', error);
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (error) {
      prompt('Copy this link', url);
      logger.warn('Clipboard copy failed, fallback to prompt:', error);
    }
  }, []);

  const openPlannerRef = useRef(
    debounce((openFn: (id: string) => void, id: string) => openFn(id), 500, {
      leading: true,
      trailing: false,
    }),
  );

  useEffect(
    () => () => {
      openPlannerRef.current.cancel();
    },
    [],
  );

  const openPlanner = useCallback(
    (id: string, onOpen: (id: string) => void) => {
      openPlannerRef.current(onOpen, id);
    },
    [],
  );

  return {
    planners,
    isRefreshing,
    refreshPlanners,
    confirmRemove,
    copyOrShareLink,
    openPlanner,
  };
}

export default useStoredPlanners;
